import React, { useState } from 'react';
import { Input, Button, Modal, ModalBody, ModalHeader, ModalFooter, } from 'reactstrap';
import { pick, isEmpty, get, difference, } from 'lodash';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useList, useAsync, } from 'react-use';
import numeral from 'numeral';
import { v4 as uuid } from 'uuid';
import { addHours, } from 'date-fns';

import firebase from '../../firebase';
import { functions } from '../../firebase';
import { batch, getCollectionData, getDocumentData, } from '../../shared/firebase';
import { basicSettingsStatus } from '../../shared/models/tenant';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import ProgressButton from '../ProgressButton';
import EditButton from '../EditButton';

const { round } = Math;
const db = firebase.firestore();
const fetchAmazonProductCandidates = functions.httpsCallable('fetchAmazonProductCandidates', { timeout: 550000, });

export default function AmazonProductsModal(props) {
  const { user, isOpen, tenant, onClickClose } = props;
  const recentSyncAmazonProductsJobs = useCollectionSubscription(tenant.ref.collection('syncAmazonProductsJobs').where('createdAt', '>=', addHours(new Date(), -1)), [tenant.id], { initialItems: null });
  const isLoading = (recentSyncAmazonProductsJobs || []).some(_ => _.status === 'initial');
  const errorJob = (recentSyncAmazonProductsJobs || []).find(_ => _.status === 'failed');
  const [keyword, setKeyword] = useState('');
  const [addedIds, setAddedIds] = useState([]);
  const completedJobsCount = (recentSyncAmazonProductsJobs || []).filter(_ => _.status === 'completed').length;
  const { value: candidates, loading: isFetchingCandidates, error: fetchCandidatesError } = useAsync(async () => {
    if (recentSyncAmazonProductsJobs == null) return null;

    const { data } = await fetchAmazonProductCandidates({ tenantId: tenant.id });
    return data.candidates;
  }, [tenant.id, recentSyncAmazonProductsJobs == null, completedJobsCount, tenant.noInventoryAllowSkusText]);
  const leftAmazonProducts = (candidates || []).filter(_ => !addedIds.includes(_.id));
  const filteredAmazonProducts = isEmpty(keyword) ? leftAmazonProducts : leftAmazonProducts.filter(_ => [_.title, _.asin, _.sku].some(_ => _?.match(keyword)));
  const [selectedItems, { set: setSelectedItems, push: selectItem, removeAt: removeSelectedItemAt }] = useList([]);
  const unselectItem = _ => removeSelectedItemAt(selectedItems.indexOf(_));
  const isSelecting = selectedItems.length > 0;
  const onClickLoad = async () => {
    try {
      await batch(db, (recentSyncAmazonProductsJobs || []).filter(_ => _.status === 'failed'), (batch, job) => batch.delete(job.ref));
      await tenant.ref.collection('syncAmazonProductsJobs').add({ status: 'initial', offset: 0, createdAt: new Date() });
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const _basicSettingsStatus = basicSettingsStatus(tenant);
  const onClickAdd = async (candidates) => {
    try {
      await candidates.reduce(async (x, candidate) => {
        await x;

        const amazonProduct = await getDocumentData(tenant.ref.collection('amazonProducts').doc(candidate.id));
        const [existingProduct] = await getCollectionData(tenant.ref.collection('products').where('sku', '==', amazonProduct.SellerSKU).limit(1));
        if(existingProduct != null) {
          throw { code: 'duplicate' };
        }

        const amount = parseInt(get(amazonProduct, 'Offers.Offer.BuyingPrice.ListingPrice.Amount'), 10);
        const ref = tenant.ref.collection('products').doc(uuid().replace(/-/g, ''));
        const netPrice = round(amount * ((tenant.netPriceRate || 90) / 100));
        if(!(netPrice >= 300)) throw new Error('卸値が300円未満');

        await ref.set({
          id: ref.id,
          tenantId: tenant.id,
          sku: amazonProduct.SellerSKU,
          createdAt: new Date(),
          updatedAt: new Date(),
          netPrice,
          amazonProduct,
          quantity: candidate.quantity,
          yahooStatus: 'initial',
          rakutenStatus: 'initial',
          wowmaStatus: 'initial',
          netPriceLogs: [
            { savedAt: new Date(), netPrice, savedBy: pick(user, ['id', 'email', 'displayName']), },
          ],
        });
        setAddedIds(_ => [..._, candidate.id]);
      }, Promise.resolve());
      toast.success('追加しました');
      setSelectedItems([]);
    } catch(e) {
      console.error(e);
      const message = e.code === 'duplicate' ? '重複登録がありました。やり直してください。' : '失敗しました';
      toast.error(message);
    }
  };

  return (
    <Modal isOpen={isOpen} style={{ minWidth: 1000 }}>
      <ModalHeader>
        Amazon商品取込
      </ModalHeader>
      <ModalBody>
        {
          fetchCandidatesError != null ? (
            <div className="alert alert-danger">
              商品の取得に失敗しました。時間をおいて再度お試しください。
            </div>
          ) : (candidates == null || isFetchingCandidates) ? (
            <span className="fas fa-spin fa-spinner" />
          ) : (
            <div>
              {
                !isSelecting ? (
                  <div>
                    <div className="mb-3 d-flex justify-content-end align-items-center gap-1">
                      {tenant.enablesLogi && <EditButton label="強制取込SKU" itemRef={tenant.ref} FormModal={ModelFormModal} formProps={{ title: '強制取込SKU', fields: { noInventoryAllowSkusText: { type: 'text', rows: 5, label: '強制取込SKU', hint: 'SKUを改行区切りで入力してください。\nここに入力したSKUは、通常は取込候補に表示されない以下の商品でも表示されます。\n・FBA在庫が0の商品\n・Amazon以外の配送（自社出荷）の商品', } }, }} />}
                      <Button color="primary" onClick={onClickLoad} disabled={isLoading}>
                        <span className={classnames('fas mr-1', { 'fa-sync': !isLoading, 'fa-spin fa-spinner': isLoading })} />
                        読み込み
                      </Button>
                    </div>
                  </div>
                ) : (
                  _basicSettingsStatus === 'accepted' ? (
                    <div className="mb-3 d-flex justify-content-end align-items-center">
                      <div className="d-flex align-items-end">
                        <div>
                          {selectedItems.length} 件を選択中
                        </div>
                        <ProgressButton className="ml-2" process={onClickAdd.bind(null, selectedItems)}>
                          <span className="fas fa-plus mr-1" />
                          追加
                        </ProgressButton>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      初期設定が完了していないため商品登録できません
                    </div>
                  )
                )
              }
              <div className="mb-3 d-flex justify-content-start align-items-center">
                <div style={{ width: 350 }}>
                  <Input type="search" value={keyword} onChange={_ => setKeyword(_.target.value)} placeholder="ASIN、SKU、商品名で検索" disabled={selectedItems.length > 0} />
                </div>
              </div>
              {
                isLoading && (
                  <div className="alert alert-info">
                    商品の読み込みには数分かかる場合もあります。
                    <br />
                    今しばらくお待ち下さいませ。
                  </div>
                )
              }
              {
                errorJob != null && (
                  <div className="alert alert-danger">
                    商品の読み込みに失敗しました。
                    {errorJob.errorMessage}
                  </div>
                )
              }
              <table className="table table-bordered">
                <thead className="thead-light text-nowrap">
                  <tr>
                    <th>
                      {
                        filteredAmazonProducts.length > 0 && (
                          <Input type="checkbox" className="position-relative m-0" checked={difference(filteredAmazonProducts, selectedItems).length === 0} onChange={_ => _.target.checked ? setSelectedItems(filteredAmazonProducts) : setSelectedItems([])} />
                        )
                      }
                    </th>
                    <th>画像</th>
                    <th>ASIN</th>
                    <th>SKU</th>
                    <th>商品名</th>
                    <th>Amazon価格</th>
                    <th>在庫数</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    filteredAmazonProducts.map((amazonProduct) => {
                      const { sku, title, imageUrl, asin, amount, quantity, } = amazonProduct;
                      return (
                        <tr key={sku}>
                          <td>
                            <Input type="checkbox" className="position-relative m-0" checked={selectedItems.includes(amazonProduct)} onChange={_ => selectedItems.includes(amazonProduct) ? unselectItem(amazonProduct) : selectItem(amazonProduct)} />
                          </td>
                          <td>
                            <img src={imageUrl} style={{ maxWidth: 80 }} />
                          </td>
                          <td>{asin}</td>
                          <td>{sku}</td>
                          <td>{title}</td>
                          <td className="text-nowrap text-right">{numeral(parseInt(amount, 10)).format('0,0')}</td>
                          <td className="text-nowrap text-right">{numeral(quantity).format('0,0')}</td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
      </ModalFooter>
    </Modal>
  );
};
